import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTour } from '../contexts/TourContext';
import { getDfmeaAppUrl, getPfmeaAppUrl } from '../config/appUrls';
import MagnusResolveAssistant from './MagnusResolveAssistant';
import Footer from './Footer';
import TourOverlay from './TourOverlay';
import { LearnSidebar } from './learn/LearnSidebar';

// Refactored Components
import { Sidebar } from './layout/Sidebar';
import { LayoutHeader } from './layout/LayoutHeader';

import {
    LayoutDashboard,
    AlertTriangle,
    FileCheck,
    GitBranch,
    BarChart3,
    Beaker,
    Brain,
    Bot,
    Shield,
    Users,
    Truck,
    Activity,
    FileText
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const Layout: React.FC = () => {
    const { user, logout } = useAuth();
    const { isDarkMode, toggleTheme } = useTheme();
    const { startTour } = useTour();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isFullScreen, setIsFullScreen] = useState(false);

    const DFMEA_URL = getDfmeaAppUrl();
    const PFMEA_URL = getPfmeaAppUrl();

    // Listen for Escape key to exit full screen
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isFullScreen) {
                setIsFullScreen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullScreen]);

    // State for collapsible nav sections
    const [expandedNavSections, setExpandedNavSections] = useState<Set<string>>(
        new Set(['🤖 AI Assistants', 'Core Modules', 'Analysis Tools'])
    );

    const toggleNavSection = (section: string) => {
        setExpandedNavSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(section)) {
                newSet.delete(section);
            } else {
                newSet.add(section);
            }
            return newSet;
        });
    };

    // Type for navigation items
    type NavSectionItem = { section: string; icon: LucideIcon };
    type NavLinkItem = { path: string; icon: LucideIcon; label: string; indented?: boolean; external?: boolean };
    type NavItem = NavSectionItem | NavLinkItem;

    const navItems: NavItem[] = [
        // AI TOOLS
        { section: '🤖 AI Assistants', icon: Bot },
        { path: '/ai-assistant', icon: Brain, label: 'Magnus AI Chat' },

        // DASHBOARD
        { section: 'Core Modules', icon: LayoutDashboard },
        { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/customer-portal', icon: Users, label: 'Customer Portal' },
        { path: '/supplier-portal', icon: Truck, label: 'Supplier Portal' },
        { path: '/fracas', icon: AlertTriangle, label: 'FRACAS Incidents' },
        { path: '/8d', icon: FileCheck, label: '8D Problem Solving' },
        { path: '/lab', icon: Beaker, label: 'Lab Testing' },
        { path: '/reports/generator', icon: FileText, label: 'Report Generator' },

        // ANALYSIS TOOLS
        { section: 'Analysis Tools', icon: Activity },
        { path: '/rca', icon: GitBranch, label: 'RCA Tools' },
        { path: '/analytics', icon: BarChart3, label: 'Analytics Hub' },

        // REPORTS & ADMIN
        { section: 'Reports & Admin', icon: FileText },
        { path: '/reports/advanced', icon: FileText, label: 'Advanced Reports' },
        { path: '/executive', icon: Activity, label: 'Executive Cockpit' },

        // CONNECTED APPS
        { section: 'Connected Apps', icon: Shield },
        { path: `${DFMEA_URL}/dashboard`, icon: LayoutDashboard, label: 'DFMEA Portal', external: true },
        { path: `${PFMEA_URL}/`, icon: Shield, label: 'PFMEA Portal', external: true },
    ];

    const currentPageLabel = (navItems.find((item): item is NavLinkItem => 'path' in item && item.path === location.pathname))?.label || 'Dashboard';

    return (
        <div className="min-h-screen bg-[var(--bg-primary)] flex">
            {/* Tour Overlay */}
            <TourOverlay />

            {/* Sidebar */}
            {!isFullScreen && (
                <Sidebar
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    expandedNavSections={expandedNavSections}
                    toggleNavSection={toggleNavSection}
                    navItems={navItems}
                    isDarkMode={isDarkMode}
                    toggleTheme={toggleTheme}
                    logout={logout}
                    user={user}
                />
            )}

            {/* Main Content Area */}
            <div className={`flex-1 ${isFullScreen ? 'ml-0' : sidebarOpen ? 'ml-64' : 'ml-16'} transition-all duration-300 flex flex-col h-screen`}>
                <LayoutHeader
                    pageTitle={currentPageLabel}
                    isFullScreen={isFullScreen}
                    setIsFullScreen={setIsFullScreen}
                    startTour={startTour}
                    isDarkMode={isDarkMode}
                    toggleTheme={toggleTheme}
                />

                {/* Main Content */}
                <main className="flex-1 overflow-auto">
                    <div className="h-full">
                        <Outlet />
                    </div>
                </main>

                <Footer />
            </div>

            {/* Floating Assistant - Always on top */}
            {!isFullScreen && <MagnusResolveAssistant />}

            {/* Magnus Academy Sidebar */}
            <LearnSidebar />
        </div>
    );
};

export default Layout;
